import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import * as React from "react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { cva, VariantProps } from "class-variance-authority"

export const modalSizeVariants = cva("", {
  variants: {
    size: {
      default: "max-w-[calc(min(100vw-2rem-2.5%,52rem))]",
      sm: "max-w-sm",
      md: "max-w-md",
      lg: "max-w-lg",
      xl: "max-w-xl",
      [`2xl`]: "max-w-2xl",
      [`3xl`]: "max-w-3xl",
      [`4xl`]: "max-w-4xl",
      [`5xl`]: "max-w-5xl",
      [`6xl`]: "max-w-6xl",
    },
  },
  defaultVariants: {
    size: "default",
  },
})

export const modalPlacementVariants = cva("", {
  variants: {
    align: {
      center: "grid place-items-center",
      top: "grid item-start justify-items-center mt-4",
    },
  },
  defaultVariants: {
    align: "center",
  },
})

export const modalHeaderVariants = cva(
  "flex justify-between items-center py-3 px-6 rounded rounded-b-none text-center sm:text-left text-primary-foreground bg-primary translate-y-[-1px]",
  {
    variants: {
      variant: {
        default: "flex justify-between items-center rounded-b-none text-center sm:text-left bg-primary",
        warning: "flex justify-between items-center rounded-b-none text-center sm:text-left bg-amber-500",
        destructive: "flex justify-between items-center rounded-b-none text-center sm:text-left bg-destructive",
        target: "flex justify-between items-center rounded-b-none text-center sm:text-left bg-target",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

export interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof modalSizeVariants>,
    VariantProps<typeof modalPlacementVariants> {}

const DialogContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, DialogContentProps>(
  ({ className, children, size, align, ...props }, ref) => (
    <DialogPortal>
      <DialogOverlay />
      <div className={cn("absolute inset-0", modalPlacementVariants({ align }))}>
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            modalSizeVariants({ size: size }),
            "overflow-y-auto max-h-[calc(100vh-2rem)] bg-background rounded fixed z-50 w-full dark:border shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            className,
          )}
          {...props}
        >
          {children}
        </DialogPrimitive.Content>
      </div>
    </DialogPortal>
  ),
)
DialogContent.displayName = DialogPrimitive.Content.displayName

export interface DialogHeaderProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof modalHeaderVariants> {}

const DialogHeader = ({ className, variant, children, ...props }: DialogHeaderProps) => (
  <div className={cn(modalHeaderVariants({ variant, className }), className)} {...props}>
    {children}
    <DialogPrimitive.Close
      asChild
      className="opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
    >
      <Button size="icon" variant={variant} className="hover:bg-white/20">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </Button>
    </DialogPrimitive.Close>
  </div>
)
DialogHeader.displayName = "DialogHeader"

const DialogBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("p-6 bg-background", className)} {...props} />
)
DialogBody.displayName = "DialogBody"

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex bg-background rounded-b flex-col-reverse px-6 py-4 sm:flex-row sm:space-x-2 border-t",
      className,
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
